import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, X, MailWarning, ChevronRight } from "lucide-react";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Banner shown on the admin dashboard whenever Resend has rejected one
 * or more outbound emails in the last 24 hours, or there are users
 * stuck with a "failed" setup-email status.
 *
 * Why this exists: Resend's free tier caps at 100 emails / day, but the
 * failure is silent unless we surface it. Previously admins discovered
 * the problem only when confused users reached out days later. Now they
 * see a red banner the moment an email is rejected, with the exact
 * Resend error string and a one-click jump to the affected users.
 */
export default function EmailHealthBanner() {
    const [health, setHealth] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/email-health");
            setHealth(data);
        } catch {
            setHealth(null);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, []);

    if (!health || dismissed) return null;
    const failures = health.last_24h_failures || 0;
    const stuck = health.stuck_setup_count || 0;
    if (failures === 0 && stuck === 0) return null;

    return (
        <div
            className="relative rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 shadow-sm"
            data-testid="email-health-banner"
            role="alert"
        >
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="absolute top-2 right-2 p-1 rounded hover:bg-amber-100 transition-colors"
                aria-label="Dismiss email health banner"
                data-testid="dismiss-email-banner"
            >
                <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
                <div className="rounded-full bg-amber-200/70 p-2 flex-shrink-0">
                    <AlertTriangle className="h-4 w-4 text-amber-900" />
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="font-medium text-sm">
                        Email delivery is failing
                    </div>
                    <div className="text-sm leading-relaxed">
                        {failures > 0 && (
                            <>
                                <strong>{failures}</strong> outbound{" "}
                                {failures === 1 ? "email was" : "emails were"} rejected by Resend in the last 24h.
                            </>
                        )}{" "}
                        {stuck > 0 && (
                            <>
                                <strong>{stuck}</strong> {stuck === 1 ? "user is" : "users are"} stuck without a setup link.
                            </>
                        )}
                    </div>
                    {health.most_recent_error && (
                        <div className="text-xs bg-amber-100 border border-amber-200 rounded px-2 py-1 font-mono break-words">
                            <MailWarning className="inline h-3 w-3 mr-1 -mt-0.5" />
                            Last error: <span className="font-medium">{health.most_recent_error}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-3 pt-1 text-xs">
                        <Link
                            to="/admin/users"
                            className="inline-flex items-center gap-1 font-medium hover:underline"
                            data-testid="email-banner-view-users"
                        >
                            View affected users
                            <ChevronRight className="h-3 w-3" />
                        </Link>
                        <a
                            href="https://resend.com/emails"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                            Open Resend dashboard
                            <ChevronRight className="h-3 w-3" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
